/**
 * MFX OS — Stage 2: compare a Customer PO against the approved quote.
 * Reads live OS quote docs defensively (quoteNum/fields/qtys|pricedQtys/totalAmount)
 * and produces the CompareResult that gates PO approval (hard rules).
 */
import type { CompareResult, CompareRow, CustomerPO, POLine, Verdict } from './types';
import { nowIso } from './core';

const str = (...vals: unknown[]): string => {
  for (const v of vals) if (v != null && String(v).trim()) return String(v).trim();
  return '';
};
const num = (...vals: unknown[]): number => {
  for (const v of vals) { const n = Number(v); if (Number.isFinite(n) && n) return n; }
  return 0;
};

export interface QuoteDoc {
  id: string;
  quoteNum?: string;
  status?: string;
  customerName?: string;
  totalAmount?: number;
  poNumber?: string;
  fields?: Record<string, unknown>;
  qtys?: { qty: unknown; ppu: unknown; total: unknown; skus?: unknown[] }[];
  pricedQtys?: { qty: unknown; ppu: unknown; total: unknown; skus?: unknown[] }[];
  [k: string]: unknown;
}

export interface QuoteTier { qty: number; ppu: number; total: number }

export function quoteTiers(q: QuoteDoc): QuoteTier[] {
  const src = (Array.isArray(q.pricedQtys) && q.pricedQtys.length ? q.pricedQtys : q.qtys) || [];
  return src.map((t) => ({ qty: num(t.qty), ppu: num(t.ppu), total: num(t.total) })).filter((t) => t.qty > 0);
}

export const quoteClient = (q: QuoteDoc) => str(q.customerName, q.fields?.custCo);
export const quoteEmail = (q: QuoteDoc) => str((q as Record<string, unknown>).poClientEmail, q.fields?.custEmail).toLowerCase();
export const quoteLabel = (q: QuoteDoc) => `${str(q.quoteNum, q.id)} — ${quoteClient(q) || 'unknown client'}`;

/** Quote statuses that count as an approved/committed quote for PO matching. */
const APPROVED_QUOTE = /^(ready|sent|won|production)/i;

/** Auto-map PO lines to quote tiers by exact quantity, flag price drift. */
export function autoMapLines(lines: POLine[], q: QuoteDoc): POLine[] {
  const tiers = quoteTiers(q);
  return lines.map((l) => {
    const tier = tiers.find((t) => t.qty === l.qty);
    if (!tier) return { ...l, mappedTo: '', mapStatus: 'unmapped' as const, quoteUnitPrice: undefined };
    const drift = Math.abs(tier.ppu - l.unitPrice) > 0.0005;
    return { ...l, mappedTo: `tier:${tier.qty}`, quoteUnitPrice: tier.ppu, mapStatus: drift ? 'mismatch' as const : 'mapped' as const };
  });
}

export function compare(po: CustomerPO, q: QuoteDoc | null, opts: { duplicate?: boolean } = {}): CompareResult {
  const rows: CompareRow[] = [];
  const blockers: string[] = [];
  const push = (field: string, label: string, quote: string, poVal: string, verdict?: Verdict) => {
    const v: Verdict = verdict ?? (!quote && !poVal ? 'info' : !quote || !poVal ? 'missing' : quote.toLowerCase() === poVal.toLowerCase() ? 'match' : 'mismatch');
    rows.push({ field, label, quote: quote || '—', po: poVal || '—', verdict: v });
  };

  if (!po.clientName) blockers.push('Client is not identified.');
  if (!q) {
    blockers.push('No approved quote is linked.');
    return { ranAt: nowIso(), quoteId: '', quoteNum: '', rows, mismatches: 0, unmappedLines: po.lines.length, blockers };
  }
  if (!APPROVED_QUOTE.test(str(q.status))) {
    blockers.push(`Linked quote status is "${str(q.status) || 'unknown'}" — not an approved/sent quote.`);
  }
  if (opts.duplicate) blockers.push(`Duplicate: PO number ${po.poNumber} already exists for this client.`);

  push('client', 'Customer', quoteClient(q), po.clientName || '');
  push('poNumber', 'Customer PO #', str(q.poNumber), po.poNumber, str(q.poNumber) && str(q.poNumber) !== po.poNumber ? 'mismatch' : 'info');
  push('quoteNum', 'Quote #', str(q.quoteNum, q.id), po.quoteNum || str(q.quoteNum, q.id), 'info');
  const qTotal = num(q.totalAmount);
  push('total', 'Total', qTotal ? String(qTotal.toFixed(2)) : '', po.total ? String(po.total.toFixed(2)) : '');
  push('terms', 'Payment terms', str(q.fields?.payTerms), po.paymentTerms || '');
  push('date', 'Requested date', str((q as Record<string, unknown>).poRequiredDate), po.requestedDate || '', 'info');
  push('shipTo', 'Ship-to', str((q as Record<string, unknown>).poShipTo), po.shipTo || '', po.shipTo ? undefined : 'missing');

  const mapped = autoMapLines(po.lines, q);
  const tiers = quoteTiers(q);
  for (const l of mapped) {
    const label = `Line: ${l.description || l.customerPart || 'item'} × ${l.qty.toLocaleString()}`;
    if (l.mapStatus === 'unmapped') {
      push(`line:${l.id}`, label, tiers.length ? `tiers: ${tiers.map((t) => t.qty.toLocaleString()).join(' / ')}` : 'no tiers', `qty ${l.qty.toLocaleString()} @ $${l.unitPrice}`, 'missing');
    } else if (l.mapStatus === 'mismatch') {
      push(`line:${l.id}`, label, `$${(l.quoteUnitPrice ?? 0).toFixed(4)}/unit`, `$${l.unitPrice.toFixed(4)}/unit`, 'mismatch');
    } else {
      push(`line:${l.id}`, label, `$${(l.quoteUnitPrice ?? 0).toFixed(4)}/unit`, `$${l.unitPrice.toFixed(4)}/unit`, 'match');
    }
  }

  const unmappedLines = mapped.filter((l) => l.mapStatus === 'unmapped').length;
  const priceMismatch = mapped.some((l) => l.mapStatus === 'mismatch');
  const mismatches = rows.filter((r) => r.verdict === 'mismatch').length;

  if (!po.lines.length) blockers.push('PO has no line items.');
  if (unmappedLines) blockers.push(`${unmappedLines} line(s) cannot be mapped to quote tiers.`);
  if (priceMismatch) blockers.push('Pricing differs from the approved quote (requires authorized exception).');
  if (!po.shipTo) blockers.push('Ship-to location is unresolved.');

  return { ranAt: nowIso(), quoteId: q.id, quoteNum: str(q.quoteNum, q.id), rows, mismatches, unmappedLines, blockers };
}
